import React from 'react';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { List, OrderedMap, Map } from 'immutable';
import {
  ColorRGBs,
  EditableCellTypes,
} from 'shared/flux/stores/cuesheet/CuesheetConstants';
import { DisplayTypes } from 'shared/constants/FlowConstants';
import TimeDisplayTypes from 'shared/constants/TimeDisplayTypes';
import TimeUtil from 'time';
import CellHelper from 'main/components/show/cuesheet/cellHelper';
import RTEReadOnly from 'shared/components/rte/rteReadOnly';
import HighlightCellHelper from 'main/components/show/cuesheet/highlightCellHelper';
import PageBreakInsert from './customPageBreakInsert';

function getPrintableTableBodies({
  userId,
  columns,
  rows,
  rowMap,
  show,
  twentyFourHourClock,
  pageBreakEditingMode,
  pageBreaks,
  scale,
  tableWidth,
  includePrivateNoteContents,
}) {
  let tableBodies = List();
  let formattedRows = List();

  rows.forEach((row, rowIndex) => {
    const item = rowMap.getIn(row.get('path'));
    const isTitleRow = item.get('titleView');
    const isGrid =
      show.get('displayType') === DisplayTypes.schedule ||
      show.get('displayType') === DisplayTypes.list;

    const cells = item.get('cells'),
      displayStartTimeClock = item.get('displayStartTimeClock'),
      displayDurationClock = item.get('displayDurationClock'),
      displayPresetClock = item.get('displayPresetClock'),
      durationInSeconds = item.get('durationInSeconds'),
      number = item.get('numberAdjusted'),
      presetDurationInSecondsAdjusted = item.get(
        'presetDurationInSecondsAdjusted',
      );

    const durationDisplayType = show.get('durationDisplayType'),
      presetDurationDisplayType = show.get('presetDurationDisplayType');

    const showStartOn = show.get('startOn');

    const itemStartOn = item.get('startOnAdjusted');

    let startTimeValue = itemStartOn || moment().startOf('day').valueOf();

    if (rowIndex === 0 && !itemStartOn) {
      startTimeValue = showStartOn;
    }

    let formattedCells = OrderedMap();

    if (isTitleRow && isGrid) {
      const titleFormat = item.get('titleFormat');
      const rteTitleState = item.get('rteTitleState');

      const itemCellClasses = classNames('item-title-cell', 'printable-cell');

      const contentNode = (
        <RTEReadOnly
          customColors={ColorRGBs}
          rteState={rteTitleState}
          placeholder={item.get('title') || ''}
          removeLinks
        />
      );

      const titleCell = (
        <td
          colSpan={columns.size}
          className={itemCellClasses}
          style={CellHelper.getFormatStyles(titleFormat)}
        >
          {contentNode}
        </td>
      );

      formattedCells = formattedCells.set(
        EditableCellTypes.ItemTitle,
        titleCell,
      );
    } else {
      columns.forEach((column, key) => {
        const id = column.get('_id');

        //////Item number
        if (key === EditableCellTypes.ItemNumber) {
          const numberCell = (
            <td className="item-number-cell printable-cell">{number}</td>
          );
          formattedCells = formattedCells.set(
            EditableCellTypes.ItemNumber,
            numberCell,
          );

          return formattedCells;
        }

        //////Start Time
        if (key === EditableCellTypes.ItemStartTime) {
          let startTimeText = '';
          if (displayStartTimeClock) {
            startTimeText = TimeUtil.getShorthandTimeUTC(
              startTimeValue,
              twentyFourHourClock,
            );
          }
          const startTimeClasses = classNames(`start-time-cell printable-cell`);
          const startTimeCell = (
            <td className={startTimeClasses}>{startTimeText}</td>
          );
          formattedCells = formattedCells.set(
            EditableCellTypes.ItemStartTime,
            startTimeCell,
          );

          return formattedCells;
        }

        //////Preset Duration
        if (key === EditableCellTypes.ItemPresetDuration) {
          let presetText = '';
          const presetValue = presetDurationInSecondsAdjusted;
          if (displayPresetClock) {
            presetText = TimeUtil.getShorthandFormattedPresetDuration(
              presetValue,
            );

            if (presetDurationDisplayType === TimeDisplayTypes.sixDigitTime) {
              presetText = TimeUtil.getFormattedDuration(presetValue);
            }

            if (presetDurationDisplayType === TimeDisplayTypes.presetTime) {
              presetText = TimeUtil.getFormattedPresetDuration(presetValue);
            }
          }
          const presetDurationClasses = classNames(
            `preset-duration-cell`,
            `printable-cell`,
            { 'cs-preset-time-cell-over': presetValue < 0 },
          );
          const presetDurationCell = (
            <td className={presetDurationClasses}>{presetText}</td>
          );
          formattedCells = formattedCells.set(
            EditableCellTypes.ItemPresetDuration,
            presetDurationCell,
          );

          return formattedCells;
        }

        //////Duration
        if (key === EditableCellTypes.ItemDuration) {
          let durationText = '';
          if (displayDurationClock) {
            const durationValue = durationInSeconds;
            durationText = TimeUtil.getShorthandDuration(durationValue);
            if (durationDisplayType === TimeDisplayTypes.clockTime) {
              durationText = TimeUtil.getShorthandFormattedPresetDuration(
                durationInSeconds,
              );
            }
            if (durationDisplayType === TimeDisplayTypes.sixDigitTime) {
              durationText = TimeUtil.getFormattedDuration(durationInSeconds);
            }
          }
          const durationClasses = classNames(`duration-cell printable-cell`);
          const durationCell = (
            <td className={durationClasses}>{durationText}</td>
          );
          formattedCells = formattedCells.set(
            EditableCellTypes.ItemDuration,
            durationCell,
          );

          return formattedCells;
        }

        const width = column.get('width');

        //////Item Title
        if (key === EditableCellTypes.ItemTitle) {
          const titleFormat = item.get('titleFormat');
          const rteTitleState = item.get('rteTitleState');

          const itemCellClasses = classNames(
            `cs-printable-cell-width-${width}`,
            'item-title-cell',
            'printable-cell',
          );

          const contentNode = (
            <RTEReadOnly
              customColors={ColorRGBs}
              rteState={rteTitleState}
              placeholder={item.get('title') || ''}
              removeLinks
            />
          );

          const titleCell = (
            <td
              className={itemCellClasses}
              style={CellHelper.getFormatStyles(titleFormat)}
            >
              {contentNode}
            </td>
          );
          formattedCells = formattedCells.set(
            EditableCellTypes.ItemTitle,
            titleCell,
          );

          return formattedCells;
        }

        //////Item Description
        if (key === EditableCellTypes.ItemDescription) {
          const descriptionFormat = item.get('descriptionFormat');
          const rteDescriptionState = item.get('rteDescriptionState');

          const descriptionCellClasses = classNames(
            `cs-printable-cell-width-${width}`,
            `item-description-cell printable-cell`,
          );

          const contentNode = (
            <RTEReadOnly
              customColors={ColorRGBs}
              rteState={rteDescriptionState}
              placeholder={item.get('description') || ''}
              removeLinks
            />
          );

          const descriptionCell = (
            <td
              className={descriptionCellClasses}
              style={CellHelper.getFormatStyles(descriptionFormat)}
            >
              {contentNode}
            </td>
          );
          formattedCells = formattedCells.set(
            EditableCellTypes.ItemDescription,
            descriptionCell,
          );

          return formattedCells;
        }

        //////Item Note
        if (key === EditableCellTypes.ItemNote) {
          const hasContent = item.hasIn(['userSettings', userId]);

          const contentNode =
            hasContent && includePrivateNoteContents ? (
              <RTEReadOnly
                customColors={ColorRGBs}
                rteState={item.getIn([
                  'userSettings',
                  userId,
                  'note',
                  'rteState',
                ])}
                placeholder=""
                removeLinks
              />
            ) : (
              <span />
            );

          const privateNotesClasses = classNames(
            `cs-printable-cell-width-${width}`,
            `item-note-cell printable-cell`,
          );
          const privateNotesFormat = hasContent
            ? item.getIn(['userSettings', userId, 'note', 'format'])
            : Map();

          const privateNotesCell = (
            <td
              className={privateNotesClasses}
              style={CellHelper.getFormatStyles(privateNotesFormat)}
            >
              {contentNode}
            </td>
          );

          formattedCells = formattedCells.set(
            EditableCellTypes.ItemNote,
            privateNotesCell,
          );

          return formattedCells;
        }

        if (id) {
          let rteNode = null;
          let format = null;

          const cell = cells.get(id);

          if (cell) {
            if (cell.get('format')) format = cell.get('format');

            rteNode = (
              <RTEReadOnly
                customColors={ColorRGBs}
                rteState={cell.get('rteState')}
                placeholder=""
                removeLinks
              />
            );
          }

          const dataCellClass = classNames(
            `cs-printable-cell-width-${width}`,
            `data-cell printable-cell`,
          );

          const formattedCell = (
            <td
              className={dataCellClass}
              style={CellHelper.getFormatStyles(format)}
            >
              {rteNode}
            </td>
          );

          formattedCells = formattedCells.set(id, formattedCell);

          return formattedCells;
        }
      });
    }

    const globalHightlightStyles = HighlightCellHelper.getGlobalHighlightStyle(
      item,
    );

    const rowClasses = classNames('pdf-export-table-row', {
      'title-row': isTitleRow,
    });

    formattedRows = formattedRows.push([
      pageBreakEditingMode && (
        <PageBreakInsert
          numCells={formattedCells.size}
          rowIndex={rowIndex}
          selected={pageBreaks.indexOf(rowIndex) >= 0}
          scale={scale}
          tableWidth={tableWidth}
        />
      ),
      <tr className={rowClasses} style={globalHightlightStyles}>
        {formattedCells}
      </tr>,
    ]);

    const hasPageBreak = pageBreaks.find((index) => {
      return parseInt(index, 10) === rowIndex + 1;
    });

    if (hasPageBreak) {
      tableBodies = tableBodies.push(formattedRows);
      formattedRows = List();
    }
  });

  if (formattedRows.size > 0) tableBodies = tableBodies.push(formattedRows);

  return tableBodies.map((tableBody) => {
    return <tbody className="pdf-export-table-body">{tableBody}</tbody>;
  });
}

export default getPrintableTableBodies;
